import type { App, User, SystemConfig, LoginResponse, ApiResponse, DashboardStats, Ad, EmailCardTemplate, DownloadConfig, DownloadPlatform, DownloadUploadResult, DownloadPublicItem, AdNetworkConfig } from '@/types';

const API_BASE = '/api';

const getToken = () => localStorage.getItem('auth_token');

const request = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
    });

    // 处理401未授权：token过期或无效，自动登出并跳转到登录页
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      window.dispatchEvent(new CustomEvent('auth-expired'));
      setTimeout(() => { window.location.href = '/login'; }, 100);
      return { success: false, data: undefined as any, message: '登录已过期，请重新登录' } as ApiResponse<T>;
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  } catch (err) {
    console.error('API request failed:', url, err);
    return { success: false, data: undefined as any, message: '网络请求失败' } as ApiResponse<T>;
  }
};

export const authApi = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () =>
    request('/auth/logout', { method: 'POST' }),
};

export const appApi = {
  getAll: () => request<App[]>('/apps'),
  getById: (id: string) => request<App>(`/apps/${id}`),
  create: (data: Partial<App>) =>
    request<App>('/apps', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<App>) =>
    request<App>(`/apps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request(`/apps/${id}`, { method: 'DELETE' }),
};

export const userApi = {
  getAll: (appId?: string) =>
    request<User[]>(`/users${appId ? `?app_id=${appId}` : ''}`),
  getById: (id: string) => request<User>(`/users/${id}`),
  delete: (id: string) =>
    request(`/users/${id}`, { method: 'DELETE' }),
};

export const adApi = {
  getAll: (appId?: string) =>
    request<Ad[]>(`/ads${appId ? `?app_id=${appId}` : ''}`),
  getById: (id: string) => request<Ad>(`/ads/${id}`),
  create: (data: Partial<Ad>) =>
    request<Ad>('/ads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<Ad>) =>
    request<Ad>(`/ads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request(`/ads/${id}`, { method: 'DELETE' }),
};

// 广告联盟配置接口（管理员）
export const adNetworkApi = {
  get: (appId: string) =>
    request<AdNetworkConfig>(`/ad-network?app_id=${appId}`),
  update: (data: Partial<AdNetworkConfig> & { app_id: string }) =>
    request<AdNetworkConfig>('/ad-network', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getPublic: (appId: string) =>
    request<AdNetworkConfig>(`/ad-network/public?app_id=${appId}`),
};

export const settingsApi = {
  get: () => request<SystemConfig>('/settings'),
  update: (data: Partial<SystemConfig>) =>
    request<SystemConfig>('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

export const dashboardApi = {
  getStats: () => request<DashboardStats>('/dashboard'),
};

// 名片模板相关接口（管理员）
export const cardApi = {
  list: () => request<EmailCardTemplate[]>('/card-templates'),
  preview: (appId?: string, templateId?: string) =>
    request<{ html: string; subject?: string; plain?: string; template: EmailCardTemplate; app_name?: string; card_template?: string; card_templates_enabled?: string[] }>('/preview-card', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, template_id: templateId }),
    }),
};

// 供第三方应用调用的API（不需要管理员Token，需要app_id+app_secret）
export const verifyApi = {
  sendCode: (appId: string, appSecret: string, email: string) =>
    request('/send-code', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, email }),
    }),
  verifyLogin: (appId: string, appSecret: string, email: string, code: string) =>
    request('/verify-login', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, email, code }),
    }),
  register: (appId: string, appSecret: string, email: string, code: string, password: string, nickname?: string) =>
    request('/register', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, email, code, password, nickname }),
    }),
  loginWithPassword: (appId: string, appSecret: string, email: string, password: string) =>
    request('/login', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, email, password }),
    }),
  resetPassword: (appId: string, appSecret: string, email: string, code: string, newPassword: string) =>
    request('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, email, code, new_password: newPassword }),
    }),
  getAds: (appId: string, position?: string) =>
    request(`/ads/public?app_id=${appId}${position ? `&position=${position}` : ''}`),
};

// 第三方程序后台用户管理API（需要app_id+app_secret，按应用隔离）
export const appUserApi = {
  getUserList: (appId: string, appSecret: string, page?: number, pageSize?: number, keyword?: string) =>
    request('/app/users', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, page, page_size: pageSize, keyword }),
    }),
  getUserDetail: (appId: string, appSecret: string, userId?: string, email?: string) =>
    request('/app/user/detail', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, user_id: userId, email }),
    }),
  updateUser: (appId: string, appSecret: string, userId: string, data: { nickname?: string; status?: string }) =>
    request('/app/user/update', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, user_id: userId, ...data }),
    }),
  toggleUserStatus: (appId: string, appSecret: string, userId: string, status: 'active' | 'disabled') =>
    request('/app/user/toggle-status', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, user_id: userId, status }),
    }),
  unlinkUser: (appId: string, appSecret: string, userId: string) =>
    request('/app/user/unlink', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, user_id: userId }),
    }),
  verifyToken: (appId: string, appSecret: string, userToken: string) =>
    request('/app/verify-token', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId, app_secret: appSecret, user_token: userToken }),
    }),
};

// 客户端下载管理接口（管理员）
export const downloadApi = {
  getAll: (appId?: string) =>
    request<DownloadConfig[]>(`/downloads${appId ? `?app_id=${appId}` : ''}`),
  create: (data: Partial<DownloadConfig>) =>
    request<DownloadConfig>('/downloads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<DownloadConfig>) =>
    request<DownloadConfig>(`/downloads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request(`/downloads/${id}`, { method: 'DELETE' }),
  upload: async (file: File, appId: string, platform: DownloadPlatform, type: 'qrcode' | 'file'): Promise<ApiResponse<DownloadUploadResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('app_id', appId);
    formData.append('platform', platform);
    formData.append('type', type);
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/downloads/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      return (await resp.json()) as ApiResponse<DownloadUploadResult>;
    } catch (err) {
      console.error('upload failed', err);
      return { success: false, data: undefined as any, message: '上传失败' };
    }
  },
  // 公开接口（供 SDK 调用，无需管理员 token）
  getPublic: (appId: string) =>
    request<DownloadPublicItem[]>(`/downloads/public/${appId}`),
};